import {
  Box,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import SettingsLayout from "../components/SettingsLayout";
import SettingsSearchField from "../components/SettingsSearchField";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import title from "global/constants/TitleConstants";
import { useTitle } from "utils/UseTitle";
import { useContext, useEffect, useRef, useState } from "react";
import { AccountContext } from "Context";
import {
  debounceEventHandler,
  initialPageFilterState,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import { useLocation } from "react-router-dom";
import { pageFilters, UserFields } from "models/interfaces";
import strings from "global/constants/StringConstants";
import { AddTeamFields, TeamData } from "types/team";
import LockedUser from "assets/icons1/LockedUser.svg";
import EditIcon from "assets/icons1/EditIcon.svg";
import Reset2FAIcon from "assets/icons1/Rest2FAIcon.svg";
import UnLockUserIcon from "assets/icons1/UnlockUserIcon.svg";
import { getRoleName, getTeamData, handleMenuItemClick } from "./TeamHandler";
import InviteTeam from "./InviteTeam";
import ReassignDocumentsDialog from "./components/ReassignDocumentsDialog";
import { hasAccessTo } from "layouts/Config";
import SeatsCard from "./components/SeatsCard";
import SeatsCardSkeleton from "./components/SeatsCardSkeleton";
import TeamTable, { TeamMemberRow } from "./components/TeamTable";
import { SendInviteIcon } from "./components/TeamIcons";
import { sendInviteButtonSx } from "./Team.styles";
import useSeatContext from "hooks/use-seat-context";
import useBillingSummary from "hooks/use-billing-summary";
import { deleteUser, updateUserStatus } from "Services/CommonServices";
import {
  formatMoney,
  formatPerSeatStrapline,
  getSeatBlockReason,
  SeatBlockReason,
} from "helpers/perSeatHelpers";
import { isBillableStatus, userStatus } from "types/perSeat";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { apiStatus } from "utils/apiStatus";
import CustomSeatBlockedDialog from "global/components/CustomSeatBlockedDialog/CustomSeatBlockedDialog";
import CustomSeatActionDialog from "global/components/CustomSeatActionDialog/CustomSeatActionDialog";

/** Role id the backend reserves for the built-in account administrator. */
const ACCOUNT_ADMIN_ROLE_ID = 1;

const splitName = (name: string) => {
  const [firstName, ...rest] = (name ?? "").split(" ");
  return { firstName: firstName ?? "", lastName: rest.join(" ") };
};

const Team = () => {
  useTitle(title.TEAM);
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const searchFromUrl = urlParams.get("search");
  const [loading, setLoading] = useState<boolean>(false);
  const [team, setTeam] = useState<TeamData[]>([]);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [count, setCount] = useState<number>();
  const [isMFAEnabled, setIsMFAEnabled] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedUser, setSelectedUser] = useState<TeamData | null>(null);
  const [selectedUserData, setSelectedUserData] = useState<AddTeamFields>();
  const [teamFilters, setTeamFilters] = useState<pageFilters>(
    initialPageFilterState(
      searchFromUrl ?? "",
      pageFromUrl,
      rowsPerPageFromUrl,
    ),
  );
  const [isEdit, setIsEdit] = useState(false);

  const [inviteUser, setInviteUser] = useState<{
    visible: boolean;
    metadata: UserFields;
  }>({
    visible: false,
    metadata: {
      email: "",
      name: "",
      firstName: "",
      lastName: "",
      contactNo: "",
      roleIds: [],
      id: 0,
    },
  });
  const { currentPlan } = useContext(AccountContext);

  const {
    seats,
    isPerSeat,
    catalogPlan,
    isLoading: isSeatsLoading,
    refresh: refreshSeats,
  } = useSeatContext(currentPlan);
  // The summary is the accurate source (Stripe's actual billed quantity), but
  // it only answers for an active subscription — a trial, a cancelled plan, or
  // a plan the backend hasn't wired up for it yet all fail the call. planLimit
  // is the fallback for all of those, so the card and the invite gate keep
  // working even when the summary has nothing to say.
  // Fetched as soon as there's a plan to ask about, not gated on per-seat or on
  // planLimit reporting a seat cap first — an uncapped plan reports no `user`
  // entity at all, which must not suppress the summary call itself.
  const {
    summary: billingSummary,
    isLoading: isBillingLoading,
    refresh: refreshBilling,
  } = useBillingSummary(Boolean(currentPlan?.id));
  /** True only while this screen is deliberately refetching the seat counts. */
  const [isRefreshingSeats, setRefreshingSeats] = useState<boolean>(false);

  // Trusted only on a per-seat plan reporting a real seat count: the summary
  // call is fired for every plan (see above), and a non-per-seat response with
  // `seats: 0` would otherwise read as "0 seats, fully occupied" — permanently
  // blocking Send Invite / Reactivate account-wide rather than falling back.
  const trustBillingSummary =
    isPerSeat && Boolean(billingSummary) && billingSummary!.seats > 0;
  const seatCard = trustBillingSummary
    ? { used: billingSummary!.activeUsers, total: billingSummary!.seats }
    : seats.limit > 0
      ? { used: seats.used, total: seats.limit }
      : null;
  /*
    The skeleton covers two cases, and only two: neither source has answered yet
    (first load — it fills the gap so the card doesn't pop in and shove the
    table down), and a refresh this screen asked for after an invite or a seat
    action, where the meter would otherwise sit on the pre-action count.

    Deliberately NOT "whenever either source is loading": if one of the seat
    calls is slow or never settles, that leaves the card shimmering for ever
    with a perfectly good number already in hand.
  */
  const isSeatsCardLoading =
    ((isSeatsLoading || isBillingLoading) && !seatCard) || isRefreshingSeats;
  const isAtUserLimit = trustBillingSummary
    ? billingSummary!.activeUsers >= billingSummary!.seats
    : seats.isFull;
  const isAdmin = hasAccessTo(strings.resources.app, strings.adminPermission);

  /** A seat action refused on purpose by the API (`406` limit / `402` card). */
  const [seatBlock, setSeatBlock] = useState<{
    reason: SeatBlockReason;
    message?: string;
  } | null>(null);
  /**
   * Opens the seat prompt for a refused seat action. `406` is also the API's
   * generic validation status ("Email … is already registered"), so it only
   * means "no free seat" when the seat meter agrees every seat is in use —
   * otherwise the API's own message is the real reason.
   */
  const showSeatBlock = (reason: SeatBlockReason, message?: string) => {
    if (reason === "limit" && !isAtUserLimit) {
      handleErrorMessage({
        statusCode: apiStatus.seatLimitReached,
        message,
      } as any);
      return;
    }
    setSeatBlock({ reason, message });
  };
  /** Seat action awaiting confirmation. */
  const [seatAction, setSeatAction] = useState<{
    type: "deactivate" | "reactivate" | "delete";
    user: TeamData;
  } | null>(null);
  const [isSeatActionRunning, setSeatActionRunning] = useState<boolean>(false);
  /** User whose documents are being reassigned (offboarding custody transfer). */
  const [reassignUser, setReassignUser] = useState<TeamData | null>(null);

  const userActionMenu = (selectedUserStatus: string) => {
    const isUserLocked = selectedUserStatus === userStatus.locked;
    const isBillable = isBillableStatus(selectedUserStatus);
    return [
      {
        label: "Edit",
        value: "edit",
        icon: <img src={EditIcon} alt="edit" aria-hidden="true" />,
      },
      {
        label: "Unlock User",
        value: "unlock",
        icon: (
          <img
            src={isUserLocked ? LockedUser : UnLockUserIcon}
            alt="unlock"
            aria-hidden="true"
          />
        ),
        disabled: !isUserLocked,
      },
      {
        label: "Reset 2FA",
        value: "reset2FA",
        icon: <img src={Reset2FAIcon} alt="reset2FA" aria-hidden="true" />,
        disabled: !isMFAEnabled,
      },
      // Seat actions. Deactivating frees a seat; reactivating consumes one, so
      // with no free seat it gets the same heads-up Send Invite gives rather
      // than a click that just round-trips to a 406.
      {
        label: isBillable ? "Deactivate" : "Reactivate",
        value: isBillable ? "deactivate" : "reactivate",
        tooltip:
          !isBillable && isAtUserLimit
            ? "Every seat you've paid for is in use. Buy more seats or upgrade."
            : undefined,
      },
      {
        label: "Reassign documents",
        value: "reassign",
      },
      {
        label: "Delete",
        value: "delete",
      },
    ];
  };

  /** Seat actions are handled here; everything else goes to the shared handler. */
  const SEAT_ACTIONS = ["deactivate", "reactivate", "delete"];

  /**
   * The seat card and the invite gate read `activeUsers` from whichever source
   * they currently trust (see `trustBillingSummary`), so both have to refresh:
   * a non-per-seat or summary-less plan reads `seats.*`, which only
   * `refreshSeats` (not `refreshBilling`) updates.
   */
  const refreshSeatCounts = async () => {
    try {
      setRefreshingSeats(true);
      await Promise.all([refreshBilling(), refreshSeats()]);
    } finally {
      setRefreshingSeats(false);
    }
  };

  const reloadTeam = async () => {
    // Both refetches start together and toggle their own skeleton
    // independently (the table's via `setLoading`, the seats card's inside
    // `refreshSeatCounts`) — one running long must not hold the other's data
    // back from showing the moment it lands.
    await Promise.all([
      getTeamData(
        setLoading,
        keepRawTeamData,
        setRoles,
        setCount,
        setTeam,
        teamFilters,
      ),
      // Deactivating, reactivating or deleting a user changes `activeUsers`.
      refreshSeatCounts(),
    ]);
  };

  const runSeatAction = async () => {
    if (!seatAction) return;
    const { type, user } = seatAction;
    try {
      setSeatActionRunning(true);
      if (type === "deactivate") {
        await updateUserStatus(user.id, userStatus.inactive);
        openSuccessNotification(`${user.name} has been deactivated.`);
      } else if (type === "reactivate") {
        await updateUserStatus(user.id, userStatus.active);
        openSuccessNotification(`${user.name} has been reactivated.`);
      } else {
        await deleteUser(user.email);
        openSuccessNotification(`${user.name} has been removed.`);
      }
      setSeatAction(null);
      await reloadTeam();
      // Offboarding: a departing user's requests still belong to them by email, so offer to hand
      // them to another user once they've been deactivated or removed.
      if (type === "delete" || type === "deactivate") {
        setReassignUser(user);
      }
    } catch (error: any) {
      setSeatAction(null);
      // 406 (plan full) and 402 (card declined) are deliberate outcomes with
      // their own way out — everything else is a genuine error.
      const reason = getSeatBlockReason(error);
      if (reason) {
        showSeatBlock(reason, error?.message);
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setSeatActionRunning(false);
    }
  };

  /**
   * Copy for the three user actions. None of them bills anything: the account
   * pays for seats, not for who occupies them, so these must never mention a
   * charge, credit or refund (§5).
   */
  const seatActionCopy = () => {
    const name = seatAction?.user?.name ?? "this user";
    if (seatAction?.type === "reactivate") {
      return {
        title: `Reactivate ${name}`,
        description: isPerSeat
          ? "This user will take one of your seats again. There's nothing to pay — the seat is already yours."
          : "This user will regain access straight away.",
        confirmLabel: "Reactivate",
      };
    }
    if (seatAction?.type === "delete") {
      return {
        title: `Delete ${name}`,
        description: isPerSeat
          ? "This permanently removes the user and frees their seat for someone else. Your billing doesn't change."
          : "This permanently removes the user.",
        confirmLabel: "Delete",
      };
    }
    return {
      title: `Deactivate ${name}`,
      description: isPerSeat
        ? "This frees their seat for someone else. Your billing doesn't change — release seats from “Manage seats” if you want to pay for fewer."
        : "This user will lose access until you reactivate them.",
      confirmLabel: "Deactivate",
    };
  };

  /**
   * The table renders straight from the API shape, so the shared fetch helpers
   * hand their response through untouched instead of pre-building cells.
   */
  const keepRawTeamData = (users: TeamData[]) => users;

  useEffect(() => {
    const { page, rowsPerPage, searchText } = teamFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}`,
    );
    getTeamData(
      setLoading,
      keepRawTeamData,
      setRoles,
      setCount,
      setTeam,
      teamFilters,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFilters]);

  const getFormattedRoles = (roleIds: number[]) =>
    (roleIds ?? []).map((roleId: number) =>
      roleId === ACCOUNT_ADMIN_ROLE_ID
        ? { id: roleId, name: "Account Admin" }
        : { id: roleId, name: getRoleName(roleId, roles) },
    );

  const memberRows: TeamMemberRow[] = team.map((data: TeamData) => {
    const { firstName, lastName } = splitName(data.name);
    const memberRoles = getFormattedRoles(data.roleIds);
    // The design shows a single role pill; extra roles collapse into "+N".
    const roleLabel = memberRoles.length
      ? `${memberRoles[0].name}${
          memberRoles.length > 1 ? ` +${memberRoles.length - 1}` : ""
        }`
      : "";

    return {
      id: data.id,
      name: data.name,
      initials:
        `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "?",
      phone: data.contactNo ?? "",
      email: data.email,
      roleLabel,
      status: data.status,
    };
  });

  const handleActionClick = (
    event: React.MouseEvent<HTMLElement>,
    member: TeamMemberRow,
  ) => {
    const data = team.find((user: TeamData) => user.id === member.id);
    if (!data) return;
    const { firstName, lastName } = splitName(data.name);
    setSelectedUser(data);
    setSelectedUserData({
      account: data.account,
      name: data.name,
      firstName,
      lastName,
      email: data.email,
      contactNo: data.contactNo!,
      roleIds: getFormattedRoles(data.roleIds),
      id: data.id,
    });
    setIsMFAEnabled(!!data.multiFactorLogin);
    setAnchorEl(event.currentTarget);
  };

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setTeamFilters({
      ...teamFilters,
      page: 1,
      searchText,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setTeamFilters({
      ...teamFilters,
      page: newPage,
    });
  };

  const getInviteUser = () => {
    return (
      <InviteTeam
        user={inviteUser.metadata}
        allRoles={roles}
        visible={inviteUser.visible}
        isEdit={isEdit}
        handleDialogClose={() => {
          setInviteUser({
            visible: false,
            metadata: {
              email: "",
              name: "",
              firstName: "",
              lastName: "",
              id: 0,
              roleIds: [],
            },
          });
        }}
        setInviteUser={setInviteUser}
        convertDataToTableFormat={keepRawTeamData}
        setRoles={setRoles}
        setCount={setCount}
        setTeam={setTeam}
        teamFilters={teamFilters}
        onSeatBlocked={showSeatBlock}
        onSeatsChanged={refreshSeatCounts}
        setTableLoading={setLoading}
      />
    );
  };

  // Seats bought and in use come from the billing summary when it answers —
  // the actual billing basis, not plan-limit's capacity snapshot — and from
  // planLimit otherwise. Neither one having anything (an uncapped plan reports
  // no seat entity at all) is the only case with nothing to show — but only
  // once both sources have actually finished, not just because they're empty
  // so far.
  const getSeatsCard = () => {
    if (isSeatsCardLoading) return <SeatsCardSkeleton />;
    return seatCard ? (
      <SeatsCard
        used={seatCard.used}
        total={seatCard.total}
        canManageSeats={isPerSeat}
        perSeatNote={
          isPerSeat && catalogPlan
            ? billingSummary
              ? `${formatPerSeatStrapline(catalogPlan)} · ${formatMoney(
                  billingSummary.subtotal,
                  billingSummary.currency,
                )} at next renewal`
              : `${formatPerSeatStrapline(catalogPlan)} · ${formatMoney(
                  seatCard.total * (Number(catalogPlan.price) || 0),
                  catalogPlan.currency,
                )} at next renewal`
            : undefined
        }
        // Seat count changes only happen on the plan page now — this screen
        // just fills the seats that are already bought.
        onManageSeats={() => history.push(viewpaths.viewMyPlanViewPath)}
      />
    ) : null;
  };

  const sendInviteAction = isAdmin ? (
    <Tooltip
      title={
        isAtUserLimit
          ? "Every seat you've paid for is in use. Buy more seats or upgrade."
          : ""
      }
      arrow
      describeChild
    >
      <Box component="span">
        <Button
          id="send_invite"
          disableRipple
          onClick={() => {
            // With no free seat the API would answer 406, so offer the way out
            // straight away rather than after a round trip.
            if (isAtUserLimit) {
              setSeatBlock({ reason: "limit" });
              return;
            }
            setIsEdit(false);
            setInviteUser({
              ...inviteUser,
              visible: true,
            });
          }}
          // Held disabled until the seat data has actually loaded — before
          // that, `isAtUserLimit` is a guess, and a click could either invite
          // past a full account or block one that has room.
          disabled={loading || isSeatsCardLoading}
          startIcon={<SendInviteIcon />}
          sx={sendInviteButtonSx}
        >
          Send Invite
        </Button>
      </Box>
    </Tooltip>
  ) : undefined;

  // @ts-ignore
  // @ts-ignore
  return (
    <SettingsLayout
      activeTab="Teams"
      title="Team"
      // Both small references open with the seat meter and put the search box
      // and "Send Invite" under it; the cards carry the matching orders.
      headerOrder={2}
      headerAction={
        <>
          <SettingsSearchField
            id="team_search_box"
            placeholder="Search team"
            inputRef={searchTerm}
            onChange={debounceEventHandler(
              searchChangeHandler,
              strings.SEARCH_TIME_OUT,
            )}
          />
          {sendInviteAction}
        </>
      }
    >
      {getSeatsCard()}
      <TeamTable
        members={memberRows}
        loading={loading}
        rowsPerPage={teamFilters.rowsPerPage ?? rowsPerPageFromUrl}
        page={teamFilters.page ?? 1}
        count={count ?? 0}
        onActionClick={isAdmin ? handleActionClick : undefined}
        openMenuMemberId={anchorEl ? (selectedUser?.id ?? null) : null}
        onPageChange={pageChangeHandler}
      />
      {getInviteUser()}
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedUser(null);
        }}
      >
        {selectedUser &&
          userActionMenu(selectedUser.status).map(
            (item: any, index: number) => (
              <Tooltip
                key={index}
                title={item.tooltip ?? ""}
                placement="left"
                arrow
              >
                <MenuItem
                  value={item.value}
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.value === "reassign") {
                      setAnchorEl(null);
                      setReassignUser(selectedUser);
                      return;
                    }
                    if (item.value === "reactivate" && isAtUserLimit) {
                      // Same refusal a click here would earn from the API
                      // (406, no free seat) — skip the round trip.
                      setAnchorEl(null);
                      setSeatBlock({ reason: "limit" });
                      return;
                    }
                    if (SEAT_ACTIONS.includes(item.value)) {
                      setAnchorEl(null);
                      setSeatAction({ type: item.value, user: selectedUser });
                      return;
                    }
                    handleMenuItemClick(
                      item.value,
                      setAnchorEl,
                      setLoading,
                      keepRawTeamData,
                      setRoles,
                      setCount,
                      setTeam,
                      teamFilters,
                      selectedUser,
                      selectedUserData,
                      setInviteUser,
                      setIsEdit,
                    );
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {item.icon}
                    <Typography variant="body2" component="span">
                      {item.label}
                    </Typography>
                  </Box>
                </MenuItem>
              </Tooltip>
            ),
          )}
      </Menu>

      <ReassignDocumentsDialog
        fromUser={reassignUser}
        onClose={() => setReassignUser(null)}
        onDone={() => setReassignUser(null)}
      />

      <CustomSeatActionDialog
        isDialogOpen={Boolean(seatAction)}
        title={seatActionCopy().title}
        description={seatActionCopy().description}
        confirmLabel={seatActionCopy().confirmLabel}
        isSubmitting={isSeatActionRunning}
        onConfirm={runSeatAction}
        onClose={() => setSeatAction(null)}
      />

      <CustomSeatBlockedDialog
        reason={seatBlock?.reason ?? null}
        message={seatBlock?.message}
        onClose={() => setSeatBlock(null)}
        onUpgrade={() => history.push(viewpaths.viewMyPlanViewPath)}
        onBuySeats={
          isPerSeat
            ? () => history.push(viewpaths.viewMyPlanViewPath)
            : undefined
        }
        onUpdateCard={() => history.push(viewpaths.viewMyPlanViewPath)}
      />
    </SettingsLayout>
  );
};
export default Team;
